import { useQuery } from '@tanstack/react-query';
import { api } from './client';
import { type AnalyticsRangeKey, qk } from './keys';

/**
 * Time-series payloads from `/admin/analytics/...`. Each `points[]` entry is
 * one bucket; `t` is the bucket-start ISO timestamp, `avg` / `max` / `total`
 * are pre-aggregated server-side. The chart components feed `points` straight
 * into Recharts.
 */
export interface AnalyticsRangeResponse<P> {
  from: string;
  to: string;
  bucket: 'minute' | 'hour' | 'day';
  bucketSeconds: number;
  points: P[];
}

export interface ListenerPoint {
  t: string;
  avg: number | null;
  max: number | null;
  count: number;
}
export interface ListenerHistory extends AnalyticsRangeResponse<ListenerPoint> {
  channelSlug: string;
}

export interface LiveViewerPoint {
  t: string;
  avg: number | null;
  max: number | null;
  count: number;
}
export interface LiveViewerHistory extends AnalyticsRangeResponse<LiveViewerPoint> {
  programmeSlug: string;
}

export interface EpisodeViewPoint {
  t: string;
  total: string | null;
  count: number;
}
export interface EpisodeViewHistory extends AnalyticsRangeResponse<EpisodeViewPoint> {
  episodeId: string;
}

export interface AnalyticsOverview {
  latestListeners: Array<{
    slug: string;
    name: string;
    listeners: number | null;
    streaming: 0 | 1 | null;
    sampledAt: string | null;
  }>;
  last24hAvg: Array<{
    slug: string;
    avg24h: string | null;
    max24h: number | null;
  }>;
  liveNow: Array<{
    videoId: string;
    viewers: number | null;
    sampledAt: string;
  }>;
}

/**
 * Map a UI-side range key to absolute `from` / `to` ISO timestamps, anchored
 * on `now`. We don't pass the key to the server; the server derives a sensible
 * bucket size from the range span anyway.
 */
function rangeToParams(range: AnalyticsRangeKey): { from: string; to: string } {
  const now = Date.now();
  const ms =
    range === '1h' ? 3600_000
    : range === '24h' ? 24 * 3600_000
    : range === '7d' ? 7 * 86400_000
    : 30 * 86400_000;
  return { from: new Date(now - ms).toISOString(), to: new Date(now).toISOString() };
}

export function useAnalyticsOverview() {
  return useQuery({
    queryKey: qk.analyticsOverview(),
    queryFn: () => api.get<AnalyticsOverview>('/admin/analytics/overview'),
    refetchInterval: 60_000,
  });
}

export function useListenerHistory(channelSlug: string | null, range: AnalyticsRangeKey) {
  return useQuery({
    queryKey: qk.analyticsListeners(channelSlug ?? '', range),
    enabled: !!channelSlug,
    queryFn: () =>
      api.get<ListenerHistory>(
        `/admin/analytics/channels/${encodeURIComponent(channelSlug ?? '')}/listeners`,
        rangeToParams(range),
      ),
  });
}

export function useLiveViewerHistory(programmeSlug: string | null, range: AnalyticsRangeKey) {
  return useQuery({
    queryKey: qk.analyticsLiveViewers(programmeSlug ?? '', range),
    enabled: !!programmeSlug,
    queryFn: () =>
      api.get<LiveViewerHistory>(
        `/admin/analytics/programmes/${encodeURIComponent(programmeSlug ?? '')}/live-viewers`,
        rangeToParams(range),
      ),
  });
}

export function useEpisodeViewHistory(episodeId: string | null, range: AnalyticsRangeKey) {
  return useQuery({
    queryKey: qk.analyticsEpisodeViews(episodeId ?? '', range),
    enabled: !!episodeId,
    queryFn: () =>
      api.get<EpisodeViewHistory>(
        `/admin/analytics/episodes/${encodeURIComponent(episodeId ?? '')}/views`,
        rangeToParams(range),
      ),
  });
}

// ---------- Phase 3: client-reported event aggregates ----------

export interface CountUniquesPoint {
  t: string;
  count: number;
  uniques: number;
}
export interface CountUniquesHistory extends AnalyticsRangeResponse<CountUniquesPoint> {}

export interface TopPostItem {
  slug: string;
  views: number;
  uniques: number;
  avgDwellSeconds: number | null;
}
export interface TopPostsResponse {
  from: string;
  to: string;
  items: TopPostItem[];
}

export interface PushCtrItem {
  pushId: string;
  title: string | null;
  locale: string | null;
  deliveredAt: string | null;
  delivered: number;
  taps: number;
  uniqueTaps: number;
  /** Unique-taps / delivered. Null when delivered=0 (draft / unsent push). */
  ctr: number | null;
}
export interface PushCtrResponse {
  from: string;
  to: string;
  items: PushCtrItem[];
}

export interface PlayTimePoint {
  t: string;
  totalSeconds: number;
  sessions: number;
  uniques: number;
}
export interface PlayTimeHistory extends AnalyticsRangeResponse<PlayTimePoint> {
  scope: { type: string; id: string | null } | null;
}

export function usePostViewHistory(range: AnalyticsRangeKey) {
  return useQuery({
    queryKey: qk.analyticsPostViews(range),
    queryFn: () =>
      api.get<CountUniquesHistory>('/admin/analytics/post-views', rangeToParams(range)),
  });
}

export function useTopPosts(range: AnalyticsRangeKey, limit = 10) {
  return useQuery({
    queryKey: qk.analyticsTopPosts(range),
    queryFn: () =>
      api.get<TopPostsResponse>('/admin/analytics/post-views/top', {
        ...rangeToParams(range),
        limit: String(limit),
      }),
  });
}

export function useSessionHistory(range: AnalyticsRangeKey) {
  return useQuery({
    queryKey: qk.analyticsSessions(range),
    queryFn: () =>
      api.get<CountUniquesHistory>('/admin/analytics/sessions', rangeToParams(range)),
  });
}

export function usePushCtr(range: AnalyticsRangeKey) {
  return useQuery({
    queryKey: qk.analyticsPushCtr(range),
    queryFn: () =>
      api.get<PushCtrResponse>('/admin/analytics/push-ctr', rangeToParams(range)),
  });
}

export function usePlayTimeHistory(
  range: AnalyticsRangeKey,
  opts?: { scopeType?: 'channel' | 'episode' | 'youtube'; scopeId?: string },
) {
  return useQuery({
    queryKey: qk.analyticsPlayTime(range, opts?.scopeType, opts?.scopeId),
    queryFn: () => {
      const params: Record<string, string> = rangeToParams(range);
      if (opts?.scopeType) params.scopeType = opts.scopeType;
      if (opts?.scopeId) params.scopeId = opts.scopeId;
      return api.get<PlayTimeHistory>('/admin/analytics/play-time', params);
    },
  });
}

export interface GeoPoint {
  city: string | null;
  countryIso: string | null;
  lat: number;
  lng: number;
  sessions: number;
  uniques: number;
}
export interface GeoReachResponse {
  from: string;
  to: string;
  days: number;
  locale: string | null;
  points: GeoPoint[];
}

export function useGeoReach(days: number, locale?: string) {
  return useQuery({
    queryKey: qk.analyticsGeo(days, locale),
    queryFn: () => {
      const params: Record<string, string> = { days: String(days) };
      if (locale) params.locale = locale;
      return api.get<GeoReachResponse>('/admin/analytics/geo', params);
    },
  });
}

export interface SessionsHeatmapCell {
  /** 0=Sunday..6=Saturday (Africa/Lagos) */
  dow: number;
  /** 0..23 (Africa/Lagos) */
  hour: number;
  sessions: number;
  uniques: number;
}
export interface SessionsHeatmapResponse {
  from: string;
  to: string;
  days: number;
  timezone: string;
  cells: SessionsHeatmapCell[];
}

export function useSessionsHeatmap(days: number) {
  return useQuery({
    queryKey: qk.analyticsSessionsHeatmap(days),
    queryFn: () =>
      api.get<SessionsHeatmapResponse>('/admin/analytics/sessions-heatmap', {
        days: String(days),
      }),
  });
}

export interface PushSendTimePoint {
  /** 0..23 (Africa/Lagos) */
  hour: number;
  sent: number;
  delivered: number;
  taps: number;
  /** taps / delivered when delivered > 0; null otherwise. */
  ctr: number | null;
}
export interface PushSendTimeResponse {
  from: string;
  to: string;
  days: number;
  timezone: string;
  points: PushSendTimePoint[];
}

export function usePushSendTimeCtr(days: number) {
  return useQuery({
    queryKey: qk.analyticsPushSendTimeCtr(days),
    queryFn: () =>
      api.get<PushSendTimeResponse>('/admin/analytics/push-send-time-ctr', {
        days: String(days),
      }),
  });
}

export interface DeviceBucket {
  group: string;
  uniques: number;
}
export interface DeviceBreakdownResponse {
  from: string;
  to: string;
  days: number;
  byOs: DeviceBucket[];
  byOsVersion: DeviceBucket[];
  byDeviceModel: DeviceBucket[];
  /** Total unique devices in the period, regardless of payload completeness. */
  totalDevices: number;
  /** Subset of devices whose sessions never carried `device_model` (older
   *  app builds). Used to caption the gap in the panel UI. */
  devicesMissingDetails: number;
}

export function useDeviceBreakdown(days: number) {
  return useQuery({
    queryKey: qk.analyticsDeviceBreakdown(days),
    queryFn: () =>
      api.get<DeviceBreakdownResponse>('/admin/analytics/device-breakdown', {
        days: String(days),
      }),
  });
}
